'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createNextStepTemplate,
  deleteNextStepTemplate,
  draftNextStepTemplate,
  getNextStepTemplates,
  updateNextStepTemplate,
  type NextStepTemplate,
  type NextStepTemplateInput
} from '@/lib/api/provider/nextStepTemplates';
import { queryKeys } from '@/lib/query';

export const useNextStepTemplates = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: queryKeys.provider.nextStepTemplates.list(),
    queryFn: getNextStepTemplates,
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true
  });

export const useCreateNextStepTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: NextStepTemplateInput) => createNextStepTemplate(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.nextStepTemplates.all()
      });
    }
  });
};

export const useUpdateNextStepTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: Partial<NextStepTemplateInput> & { id: number }) =>
      updateNextStepTemplate(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.nextStepTemplates.all()
      });
    }
  });
};

export const useDeleteNextStepTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteNextStepTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.provider.nextStepTemplates.all()
      });
    }
  });
};

export const useDraftNextStepTemplate = () =>
  useMutation({
    mutationFn: (input: {
      brief: string;
      title?: string;
      actionType: NextStepTemplate['actionType'];
    }) => draftNextStepTemplate(input)
  });

export type { NextStepTemplate };
